import React, { useMemo, useState } from "react";

function normalizeText(value) {
  return String(value || "").trim();
}

function getDocumentStatus(document) {
  const storedStatus = normalizeText(
    document?.status
  ).toLowerCase();

  if (storedStatus === "archived") {
    return "archived";
  }

  if (storedStatus === "expired") {
    return "expired";
  }

 if (document?.expirationDate) {
  const rawExpiration =
    document.expirationDate;

  const expirationDate =
    typeof rawExpiration === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(
      rawExpiration
    )
      ? new Date(
          `${rawExpiration}T23:59:59.999`
        )
      : typeof rawExpiration?.toDate ===
        "function"
      ? rawExpiration.toDate()
      : new Date(rawExpiration);

  if (
    !Number.isNaN(expirationDate.getTime()) &&
    expirationDate.getTime() < Date.now()
  ) {
    return "expired";
  }
} 

  return storedStatus || "active";
}

function formatDate(value) {
  if (!value) return "No date";

  const parsed =
    typeof value?.toDate === "function"
      ? value.toDate()
      : new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "No date";
  }

  return parsed.toLocaleDateString();
}

function emptyDocument() {
  return {
    name: "",
    type: "contract",
    status: "active",
    fileUrl: "",
    effectiveDate: "",
    expirationDate: "",
    notes: "",
  };
}

function DocumentField({
  label,
  value,
  onChange,
  type = "text",
  placeholder = "",
}) {
  return (
    <label style={{ display: "grid", gap: 5 }}>
      <span
        style={{
          color: "#475569",
          fontSize: 11,
          fontWeight: 850,
        }}
      >
        {label}
      </span>

      <input
        type={type}
        value={value || ""}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{
          width: "100%",
          minHeight: 38,
          padding: "8px 10px",
          boxSizing: "border-box",
          borderRadius: 10,
          border: "1px solid #cbd5e1",
          background: "#ffffff",
          color: "#0f172a",
          fontSize: 12,
          fontWeight: 700,
        }}
      />
    </label>
  );
}

export default function Facility360Documents({
  facility,
  onChangeFacility,
}) {
  const [draft, setDraft] = useState(emptyDocument());

  const documents = useMemo(
    () =>
      Array.isArray(facility?.documents)
        ? facility.documents
        : [],
    [facility]
  );

const activeCount = documents.filter(
  (document) =>
    getDocumentStatus(document) === "active"
).length;

const expiredCount = documents.filter(
  (document) =>
    getDocumentStatus(document) === "expired"
).length;

  function updateDraft(key, value) {
    setDraft((previous) => ({
      ...previous,
      [key]: value,
    }));
  }

  function addDocument() {
    if (!normalizeText(draft.name)) {
      window.alert("Enter a document name.");
      return;
    }

    const documentRecord = {
      id: `facility-document-${Date.now()}`,
      name: normalizeText(draft.name),
      type: normalizeText(draft.type) || "other",
      status: normalizeText(draft.status) || "active",
      fileUrl: normalizeText(draft.fileUrl),
      effectiveDate: draft.effectiveDate || "",
      expirationDate: draft.expirationDate || "",
      notes: normalizeText(draft.notes),
      addedAt: new Date().toISOString(),
    };

    onChangeFacility?.((previous) => ({
      ...previous,
      documents: [
        ...(Array.isArray(previous?.documents)
          ? previous.documents
          : []),
        documentRecord,
      ],
    }));

    setDraft(emptyDocument());
  }

function removeDocument(documentId) {
  if (!documentId) {
    window.alert(
      "This legacy document record has no ID and cannot be removed safely."
    );
    return;
  }

  const confirmed = window.confirm(
    "Remove this document record?"
  );

  if (!confirmed) return;

  onChangeFacility?.((previous) => ({
    ...previous,
    documents: (
      Array.isArray(previous?.documents)
        ? previous.documents
        : []
    ).filter(
      (document) => document?.id !== documentId
    ),
  }));
}

  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div>
        <div
          style={{
            color: "#0f172a",
            fontSize: 20,
            fontWeight: 950,
          }}
        >
          Facility Documents
        </div>

       <div
  style={{
    marginTop: 4,
    color: "#64748b",
    fontSize: 12,
    fontWeight: 700,
  }}
>
  Facility document records for contracts,
  licenses, insurance, compliance, and
  operational agreements.
</div>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(3, minmax(0, 1fr))",
          gap: 9,
        }}
      >
        {[
          ["Documents", documents.length, "#1d4ed8"],
          ["Active", activeCount, "#166534"],
          ["Expired", expiredCount, "#b91c1c"],
        ].map(([label, value, color]) => (
          <div
            key={label}
            style={{
              padding: 13,
              borderRadius: 14,
              border: "1px solid #e2e8f0",
              background: "#f8fafc",
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 10,
                fontWeight: 900,
                textTransform: "uppercase",
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 5,
                color,
                fontSize: 21,
                fontWeight: 950,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: 14,
          borderRadius: 16,
          border: "1px solid #dbeafe",
          background: "#eff6ff",
        }}
      >
        <div
          style={{
            marginBottom: 12,
            color: "#1e3a8a",
            fontSize: 14,
            fontWeight: 950,
          }}
        >
          Add Document Record
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(2, minmax(0, 1fr))",
            gap: 10,
          }}
        >
          <DocumentField
            label="Document Name"
            value={draft.name}
            placeholder="Facility agreement"
            onChange={(value) =>
              updateDraft("name", value)
            }
          />

          <label style={{ display: "grid", gap: 5 }}>
            <span
              style={{
                color: "#475569",
                fontSize: 11,
                fontWeight: 850,
              }}
            >
              Document Type
            </span>

            <select
              value={draft.type}
              onChange={(event) =>
                updateDraft("type", event.target.value)
              }
              style={{
                width: "100%",
                minHeight: 38,
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <option value="contract">Contract</option>
              <option value="license">License</option>
              <option value="insurance">Insurance</option>
              <option value="agreement">Agreement</option>
              <option value="compliance">Compliance</option>
              <option value="other">Other</option>
            </select>
          </label>

          <label style={{ display: "grid", gap: 5 }}>
            <span
              style={{
                color: "#475569",
                fontSize: 11,
                fontWeight: 850,
              }}
            >
              Status
            </span>

            <select
              value={draft.status}
              onChange={(event) =>
                updateDraft("status", event.target.value)
              }
              style={{
                width: "100%",
                minHeight: 38,
                padding: "8px 10px",
                borderRadius: 10,
                border: "1px solid #cbd5e1",
                background: "#ffffff",
                color: "#0f172a",
                fontSize: 12,
                fontWeight: 700,
              }}
            >
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
              <option value="archived">Archived</option>
            </select>
          </label>

          <DocumentField
            label="Document File URL"
            value={draft.fileUrl}
            placeholder="Optional link to uploaded document"
            onChange={(value) =>
              updateDraft("fileUrl", value)
            }
          />

          <DocumentField
            label="Effective Date"
            type="date"
            value={draft.effectiveDate}
            onChange={(value) =>
              updateDraft("effectiveDate", value)
            }
          />

          <DocumentField
            label="Expiration Date"
            type="date"
            value={draft.expirationDate}
            onChange={(value) =>
              updateDraft("expirationDate", value)
            }
          />
        </div>

        <div style={{ marginTop: 10 }}>
          <DocumentField
            label="Notes"
            value={draft.notes}
            placeholder="Document notes"
            onChange={(value) =>
              updateDraft("notes", value)
            }
          />
        </div>

        <button
          type="button"
          onClick={addDocument}
          style={{
            marginTop: 12,
            minHeight: 38,
            padding: "8px 13px",
            borderRadius: 10,
            border: "1px solid #2563eb",
            background: "#2563eb",
            color: "#ffffff",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 900,
          }}
        >
          Add Document Record
        </button>
      </div>

      {documents.length === 0 ? (
        <div
          style={{
            padding: 20,
            borderRadius: 14,
            border: "1px dashed #cbd5e1",
            background: "#f8fafc",
            color: "#64748b",
            textAlign: "center",
            fontWeight: 750,
          }}
        >
          No facility document records are available.
        </div>
      ) : (
        <div style={{ display: "grid", gap: 9 }}>
          {documents.map((document, index) => {
          const status =
  getDocumentStatus(document);

            const statusColor =
              status === "active"
                ? "#166534"
                : status === "expired"
                ? "#b91c1c"
                : "#92400e";

            return (
              <div
                key={
                  document?.id ||
                  `${document?.name}-${index}`
                }
                style={{
                  padding: 14,
                  borderRadius: 14,
                  border: "1px solid #e2e8f0",
                  background: "#ffffff",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div>
                    <div
                      style={{
                        color: "#0f172a",
                        fontSize: 14,
                        fontWeight: 950,
                      }}
                    >
                      {document?.name ||
                        "Facility Document"}
                    </div>

                    <div
                      style={{
                        marginTop: 4,
                        color: "#64748b",
                        fontSize: 11,
                        fontWeight: 750,
                      }}
                    >
                      {normalizeText(document?.type) ||
                        "Other"}
                      {" · "}
                      Effective:{" "}
                      {formatDate(
                        document?.effectiveDate
                      )}
                    </div>

                    <div
                      style={{
                        marginTop: 3,
                        color: "#64748b",
                        fontSize: 11,
                        fontWeight: 750,
                      }}
                    >
                      Expires:{" "}
                      {formatDate(
                        document?.expirationDate
                      )}
                    </div>
                  </div>

                  <span
                    style={{
                      padding: "5px 8px",
                      borderRadius: 999,
                      background: "#f1f5f9",
                      color: statusColor,
                      fontSize: 10,
                      fontWeight: 950,
                      textTransform: "uppercase",
                    }}
                  >
                    {status}
                  </span>
                </div>

                {document?.notes ? (
                  <div
                    style={{
                      marginTop: 9,
                      color: "#475569",
                      fontSize: 12,
                      fontWeight: 700,
                      lineHeight: 1.45,
                    }}
                  >
                    {document.notes}
                  </div>
                ) : null}

                <div
                  style={{
                    display: "flex",
                    gap: 8,
                    marginTop: 11,
                  }}
                >
                  {document?.fileUrl ? (
                    <a
                      href={document.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        padding: "7px 10px",
                        borderRadius: 9,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        textDecoration: "none",
                        fontSize: 11,
                        fontWeight: 900,
                      }}
                    >
                      Open File
                    </a>
                  ) : null}

                  <button
                    type="button"
                    onClick={() =>
                      removeDocument(document?.id)
                    }
                    style={{
                      padding: "7px 10px",
                      borderRadius: 9,
                      border: "1px solid #fecaca",
                      background: "#ffffff",
                      color: "#b91c1c",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 900,
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}